import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { resetCollaborateSession, resetInboxWelcomeToast } from "../../collaborate";
import { authApi } from "../api/auth";
import { useAuthStore } from "../stores/useAuthStore";

/**
 * Loads `/auth/me` when a valid access token is in the store (after hydrate or login).
 */
export function useCurrentUser() {
  const accessToken = useAuthStore((s) => s.accessToken);
  const setUser = useAuthStore((s) => s.setUser);

  return useQuery({
    queryKey: ["auth", "me", accessToken],
    queryFn: async () => {
      try {
        const user = await authApi.me();
        setUser(user);
        return user;
      } catch (err) {
        resetCollaborateSession();
        resetInboxWelcomeToast();
        useAuthStore.getState().clearSession();
        throw err;
      }
    },
    enabled: Boolean(accessToken),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();
  const setAccessToken = useAuthStore((s) => s.setAccessToken);

  return useMutation({
    mutationFn: authApi.login,
    onSuccess: async (data) => {
      resetCollaborateSession();
      resetInboxWelcomeToast();
      setAccessToken(data.accessToken);
      await queryClient.invalidateQueries({ queryKey: ["auth"] });
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  const clearSession = useAuthStore((s) => s.clearSession);

  return useMutation({
    mutationFn: authApi.logout,
    onSettled: () => {
      resetCollaborateSession();
      resetInboxWelcomeToast();
      clearSession();
      queryClient.clear();
    },
  });
}
